import { FC } from "react";
import { Button, Input, ColorPicker } from "antd";
import { Fill, Stroke, Style } from "ol/style";

interface CalquesOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  mapStyle: string;
  setMapStyle: (style: string) => void;
  showCadParcelles: boolean;
  setShowCadParcelles: (show: boolean | ((prev: boolean) => boolean)) => void;
  showCadBatiments: boolean;
  setShowCadBatiments: (show: boolean | ((prev: boolean) => boolean)) => void;
  showCadBatiProjet: boolean;
  setShowCadBatiProjet: (show: boolean | ((prev: boolean) => boolean)) => void;
  showEmptyParcelles: boolean;
  showBiensOffMarket: boolean;
  showTransactionsFao: boolean;
  showTransactionsFaoPrixM2: boolean;
  showBiensOffMarketLoyerM2: boolean;
  customSitgLayers: {[key: string]: any};
  setCustomSitgLayers: (layers: {[key: string]: any} | ((prev: {[key: string]: any}) => {[key: string]: any})) => void;
  sitgLayerInput: string;
  setSitgLayerInput: (input: string) => void;
  onAddSitgLayer: (layerName: string) => void;
  onRemoveSitgLayer: (layerName: string) => void;
  onEmptyParcellesToggle: () => void;
  onBiensOffMarketToggle: () => void;
  onTransactionsFaoToggle: () => void;
  onTransactionsFaoPrixM2Toggle: () => void;
  onBiensOffMarketLoyerM2Toggle: () => void;
  hexToRgba: (hex: string, alpha: number) => string;
  filterSidebarVisible: boolean;
  transactionsSidebarVisible?: boolean;
}

const CalquesOverlay: FC<CalquesOverlayProps> = ({
  isOpen,
  onClose,
  mapStyle,
  setMapStyle,
  showCadParcelles,
  setShowCadParcelles,
  showCadBatiments,
  setShowCadBatiments,
  showCadBatiProjet,
  setShowCadBatiProjet,
  showEmptyParcelles,
  showBiensOffMarket,
  showTransactionsFao,
  showTransactionsFaoPrixM2,
  showBiensOffMarketLoyerM2,
  customSitgLayers,
  setCustomSitgLayers,
  sitgLayerInput,
  setSitgLayerInput,
  onAddSitgLayer,
  onRemoveSitgLayer,
  onEmptyParcellesToggle,
  onBiensOffMarketToggle,
  onTransactionsFaoToggle,
  onTransactionsFaoPrixM2Toggle,
  onBiensOffMarketLoyerM2Toggle,
  hexToRgba,
  filterSidebarVisible,
  transactionsSidebarVisible,
}) => {
  const basemapOptions = [
    { key: "satellite", label: "Satellite" },
    { key: "sitg", label: "SITG" },
    { key: "mapbox", label: "Mapbox"},
    { key: "swisstopo", label: "Swisstopo"},
  ];

  const handleAddLayer = () => {
    if (sitgLayerInput.trim()) {
      onAddSitgLayer(sitgLayerInput.trim());
      setSitgLayerInput('');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 80,
        right: filterSidebarVisible ? 380 : (transactionsSidebarVisible ? 370 : 20),
        zIndex: 1000,
        background: 'rgba(255,255,255,0.9)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        borderRadius: 8,
        width: 360,
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>Calques</div>
        <Button size="small" onClick={onClose}>Fermer</Button>
      </div>
      
      {/* Base map selection */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}
      >
        {basemapOptions.map((opt) => (
          <Button
            key={opt.key}
            style={{ width: '100%' }}
            type={
              (opt.key === mapStyle)
                ? 'primary'
                : 'default'
            }
            onClick={() => {
              setMapStyle(opt.key);
              onClose();
            }}
          >
            {opt.label}
          </Button>
        ))}
      </div>
      
      <div style={{ height: 12 }} />
      
      {/* Overlay layers */}
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Couches</span>
        <span style={{ fontSize: 11, color: '#aaa' }}>Sélection multiple</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        <Button
          type={showCadParcelles ? 'primary' : 'default'}
          onClick={() => setShowCadParcelles((v) => !v)}
        >
          Parcelles
        </Button>
        <Button
          type={showCadBatiments ? 'primary' : 'default'}
          onClick={() => setShowCadBatiments((v) => !v)}
        >
          Bâtiments
        </Button>
        <Button
          type={showCadBatiProjet ? 'primary' : 'default'}
          onClick={() => setShowCadBatiProjet((v) => !v)}
        >
          Bâtiments projetés
        </Button>
        <Button
          type={showEmptyParcelles ? 'primary' : 'default'}
          onClick={onEmptyParcellesToggle}
        >
          Parcelles vides
        </Button>
        <Button
          type={showTransactionsFaoPrixM2 ? 'primary' : 'default'}
          onClick={onTransactionsFaoPrixM2Toggle}
        >
          Prix/m2 FAO
        </Button>
        <Button
          type={showBiensOffMarketLoyerM2 ? 'primary' : 'default'}
          onClick={onBiensOffMarketLoyerM2Toggle}
        >
          Loyer/m2 off-market
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginTop: 8 }}>
        <Button
          type={showBiensOffMarket ? 'primary' : 'default'}
          onClick={onBiensOffMarketToggle}
        >
          Biens off-market
        </Button>
        <Button
          type={showTransactionsFao ? 'primary' : 'default'}
          onClick={onTransactionsFaoToggle}
        >
          Transactions FAO
        </Button>
      </div>
      
      {/* SITG Layer Section */}
      <div style={{ height: 12 }} />
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
        Ajouter une <a href="https://sitg.ge.ch/search?category=data" target="_blank">couche SITG</a>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Input
          placeholder="Nom de la couche SITG"
          value={sitgLayerInput}
                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSitgLayerInput(e.target.value)}
          onPressEnter={handleAddLayer}
        />
        <Button
          type="primary"
          onClick={handleAddLayer}
        >
          Ajouter
        </Button>
      </div>
      
      {/* Display existing custom SITG layers */}
      {Object.keys(customSitgLayers).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 4 }}>
          {Object.entries(customSitgLayers).map(([layerName, layer]) => (
            <div key={layerName} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ColorPicker
                value={layer.getStyle()?.getStroke()?.getColor() || '#00FF00'}
                                 onChange={(color: any) => {
                  const newColor = color.toHexString();
                  // Update the layer's style with new color
                  const newFillColor = hexToRgba(newColor, 0.1);
                  const newStyle = new Style({
                    fill: new Fill({ color: newFillColor }),
                    stroke: new Stroke({ color: newColor, width: 1.5 }),
                  });
                  layer.setStyle(newStyle);
                  // Force re-render to update the color square
                  setCustomSitgLayers(prev => ({ ...prev }));
                }}
                size="small"
                trigger="click"
              />
              <Button
                size="small"
                type={layer.getVisible() ? 'primary' : 'default'}
                onClick={() => {
                  const newVisible = !layer.getVisible();
                  layer.setVisible(newVisible);
                  setCustomSitgLayers(prev => ({ ...prev })); // Force re-render
                }}
                style={{ flex: 1 }}
              >
                {layerName}
              </Button>
              <Button
                size="small"
                danger
                onClick={() => onRemoveSitgLayer(layerName)}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalquesOverlay; 